/**
 * Performance Monitor - Tracks FPS, memory usage, and provides adaptive quality settings
 */

interface PerformanceMetrics {
  fps: number;
  frameTime: number;
  memoryUsage: number;
  gpuTier: 'low' | 'medium' | 'high';
  webglSupport: boolean;
  devicePixelRatio: number;
}

interface QualitySettings {
  renderScale: number;
  particleCount: number;
  shaderComplexity: 'low' | 'medium' | 'high';
  animationFrameRate: number;
  enablePostProcessing: boolean;
  enableShadows: boolean;
  textureQuality: 'low' | 'medium' | 'high';
}

class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private frameCount = 0;
  private lastTime = performance.now();
  private fps = 60;
  private frameTime = 16.67;
  private metrics: PerformanceMetrics;
  private qualitySettings: QualitySettings;
  private observers: Set<(metrics: PerformanceMetrics, settings: QualitySettings) => void> = new Set();
  private monitoringActive = false;
  private animationId: number | null = null;

  private constructor() {
    this.metrics = this.initializeMetrics();
    this.qualitySettings = this.calculateOptimalSettings();
  }

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  /**
   * Initialize performance metrics
   */
  private initializeMetrics(): PerformanceMetrics {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
    
    // Detect GPU tier based on renderer info
    let gpuTier: 'low' | 'medium' | 'high' = 'medium';
    
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL).toLowerCase();
        
        // Simple GPU tier detection (can be enhanced with more comprehensive database)
        if (renderer.includes('intel') || renderer.includes('integrated') || renderer.includes('software')) {
          gpuTier = 'low';
        } else if (renderer.includes('nvidia') || renderer.includes('radeon') || renderer.includes('amd')) {
          gpuTier = 'high';
        }
      }
    }

    return {
      fps: 60,
      frameTime: 16.67,
      memoryUsage: this.getMemoryUsage(),
      gpuTier,
      webglSupport: !!gl,
      devicePixelRatio: window.devicePixelRatio,
    };
  }

  /**
   * Get memory usage if available
   */
  private getMemoryUsage(): number {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      return memory.usedJSHeapSize / memory.jsHeapSizeLimit;
    }
    return 0;
  }

  /**
   * Calculate optimal settings based on current metrics
   */
  private calculateOptimalSettings(): QualitySettings {
    const { fps, gpuTier, devicePixelRatio, webglSupport, memoryUsage } = this.metrics;

    // Base settings
    let settings: QualitySettings = {
      renderScale: 1,
      particleCount: 100,
      shaderComplexity: 'medium',
      animationFrameRate: 60,
      enablePostProcessing: true,
      enableShadows: true,
      textureQuality: 'medium',
    };

    // Adjust based on FPS
    if (fps < 30) {
      settings = {
        renderScale: 0.5,
        particleCount: 25,
        shaderComplexity: 'low',
        animationFrameRate: 30,
        enablePostProcessing: false,
        enableShadows: false,
        textureQuality: 'low',
      };
    } else if (fps < 45) {
      settings = {
        renderScale: 0.75,
        particleCount: 50,
        shaderComplexity: 'medium',
        animationFrameRate: 45,
        enablePostProcessing: false,
        enableShadows: false,
        textureQuality: 'medium',
      };
    }

    // Adjust based on GPU tier
    if (gpuTier === 'low') {
      settings.renderScale *= 0.75;
      settings.particleCount = Math.floor(settings.particleCount * 0.5);
      settings.shaderComplexity = 'low';
      settings.enablePostProcessing = false;
      settings.enableShadows = false;
    } else if (gpuTier === 'high' && fps >= 55) {
      settings.renderScale = Math.min(settings.renderScale * 1.25, devicePixelRatio);
      settings.particleCount = Math.floor(settings.particleCount * 1.5);
      settings.shaderComplexity = 'high';
    }

    // Adjust based on memory usage
    if (memoryUsage > 0.8) {
      settings.particleCount = Math.floor(settings.particleCount * 0.5);
      settings.textureQuality = 'low';
    }

    // Disable advanced features if WebGL not supported
    if (!webglSupport) {
      settings.shaderComplexity = 'low';
      settings.enablePostProcessing = false;
      settings.enableShadows = false;
      settings.renderScale = 0.5;
    }

    return settings;
  }

  /**
   * Start monitoring performance
   */
  startMonitoring(): void {
    if (this.monitoringActive) return;
    
    this.monitoringActive = true;
    this.measurePerformance();
  }

  /**
   * Stop monitoring performance
   */
  stopMonitoring(): void {
    this.monitoringActive = false;
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  /**
   * Measure current performance metrics
   */
  private measurePerformance(): void {
    if (!this.monitoringActive) return;

    const now = performance.now();
    this.frameCount++;

    // Calculate FPS every second
    if (now - this.lastTime >= 1000) {
      this.fps = Math.round((this.frameCount * 1000) / (now - this.lastTime));
      this.frameTime = (now - this.lastTime) / this.frameCount;
      
      // Update metrics
      this.metrics = {
        ...this.metrics,
        fps: this.fps,
        frameTime: this.frameTime,
        memoryUsage: this.getMemoryUsage(),
      };

      // Recalculate optimal settings
      const newSettings = this.calculateOptimalSettings();
      const settingsChanged = JSON.stringify(newSettings) !== JSON.stringify(this.qualitySettings);
      
      if (settingsChanged) {
        this.qualitySettings = newSettings;
        this.notifyObservers();
      }

      this.frameCount = 0;
      this.lastTime = now;
    }

    this.animationId = requestAnimationFrame(() => this.measurePerformance());
  }

  /**
   * Get current performance metrics
   */
  getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  /**
   * Get current quality settings
   */
  getQualitySettings(): QualitySettings {
    return { ...this.qualitySettings };
  }

  /**
   * Override quality settings manually
   */
  setQualitySettings(settings: Partial<QualitySettings>): void {
    this.qualitySettings = { ...this.qualitySettings, ...settings };
    this.notifyObservers();
  }

  /**
   * Subscribe to performance updates
   */
  subscribe(callback: (metrics: PerformanceMetrics, settings: QualitySettings) => void): () => void {
    this.observers.add(callback);
    
    // Immediately call with current values
    callback(this.metrics, this.qualitySettings);
    
    // Return unsubscribe function
    return () => {
      this.observers.delete(callback);
    };
  }

  /**
   * Notify all observers of performance changes
   */
  private notifyObservers(): void {
    this.observers.forEach(callback => {
      callback(this.metrics, this.qualitySettings);
    });
  }

  /**
   * Check if device meets minimum requirements for advanced features
   */
  supportsAdvancedFeatures(): boolean {
    return this.metrics.webglSupport && 
           this.metrics.gpuTier !== 'low' && 
           this.metrics.fps >= 30;
  }

  /**
   * Get recommended settings for a specific feature
   */
  getFeatureSettings(feature: 'background' | 'particles' | 'shaders' | 'animations'): Partial<QualitySettings> {
    const base = this.qualitySettings;
    
    switch (feature) {
      case 'background':
        return {
          renderScale: base.renderScale,
          shaderComplexity: base.shaderComplexity,
          textureQuality: base.textureQuality,
        };
      
      case 'particles':
        return {
          particleCount: base.particleCount,
          animationFrameRate: base.animationFrameRate,
        };
      
      case 'shaders':
        return {
          shaderComplexity: base.shaderComplexity,
          enablePostProcessing: base.enablePostProcessing,
        };
      
      case 'animations':
        return {
          animationFrameRate: base.animationFrameRate,
          enablePostProcessing: base.enablePostProcessing,
        };
      
      default:
        return base;
    }
  }

  /**
   * Force performance recalculation
   */
  recalculateSettings(): void {
    this.qualitySettings = this.calculateOptimalSettings();
    this.notifyObservers();
  }
}

export default PerformanceMonitor;
export type { PerformanceMetrics, QualitySettings };